import { useEffect, useRef } from 'react';
import {
  buildPagedGridQuery,
  createInitialPagedGridState,
  pagedGridReducer,
  PAGED_GRID_ACTIONS
} from '../hooks/pagedGridCore';

/**
 * Adaptador para class components que no pueden usar hooks directamente.
 */
export function createPagedGridController({ fetchPage, defaultPageSize = 10, onChange }) {
  let state = createInitialPagedGridState({ pageSize: defaultPageSize });
  let requestId = 0;

  function emit(patch) {
    state = { ...state, ...patch };
    onChange(state);
  }

  function dispatch(action) {
    state = pagedGridReducer(state, action);
    onChange(state);
  }

  async function load(overrides = {}) {
    const queryState = { ...state, ...overrides };
    const currentRequest = ++requestId;
    emit({ loading: true });

    try {
      const result = await fetchPage(buildPagedGridQuery(queryState));
      if (currentRequest !== requestId) {
        return result;
      }
      dispatch({
        type: PAGED_GRID_ACTIONS.SET_DATA,
        items: result.items,
        totalCount: result.totalCount,
        page: result.page ?? queryState.page,
        pageSize: result.pageSize ?? queryState.pageSize
      });
      return result;
    } catch (error) {
      if (currentRequest === requestId) {
        dispatch({ type: PAGED_GRID_ACTIONS.SET_ERROR, error });
      }
      throw error;
    }
  }

  return {
    getState: () => state,
    load,
    handlePageClick(event) {
      const page = event.selected + 1;
      dispatch({ type: PAGED_GRID_ACTIONS.SET_PAGE, page });
      return load({ page });
    },
    handleSearchChange(value) {
      dispatch({ type: PAGED_GRID_ACTIONS.SET_SEARCH, search: value });
      return load({ search: value, page: 1 });
    },
    submitSearch(search) {
      const trimmed = (search ?? '').trim();
      dispatch({ type: PAGED_GRID_ACTIONS.SET_SEARCH, search: trimmed });
      return load({ search: trimmed, page: 1 });
    },
    clearSearchIfNeeded(previousSearch) {
      if ((previousSearch ?? '').trim()) {
        dispatch({ type: PAGED_GRID_ACTIONS.SET_SEARCH, search: '' });
        return load({ search: '', page: 1 });
      }
      return Promise.resolve();
    },
    resetToInitial() {
      dispatch({ type: PAGED_GRID_ACTIONS.SET_SEARCH, search: '' });
      return load({ search: '', page: 1 });
    },
    handlePageSizeChange(pageSize) {
      dispatch({ type: PAGED_GRID_ACTIONS.SET_PAGE_SIZE, pageSize });
      return load({ pageSize, page: 1 });
    },
    handleFiltersChange(filters) {
      dispatch({ type: PAGED_GRID_ACTIONS.SET_FILTERS, filters });
      return load({ filters, page: 1 });
    },
    restoreState(saved) {
      dispatch({ type: PAGED_GRID_ACTIONS.RESTORE_STATE, state: saved });
      return load(saved);
    },
    async reloadAfterDelete() {
      return this.resetToInitial();
    },
    cancelInFlight() {
      requestId += 1;
    }
  };
}

export function usePagedGridEffect(controller, deps) {
  const depsRef = useRef(deps);
  depsRef.current = deps;

  useEffect(() => {
    if (!controller) {
      return undefined;
    }
    controller.load();
    return () => controller.cancelInFlight();
  }, deps);
}
